import { DbEnum } from "../../../../generated/prisma-enums";
import { type NexusApiActionDef } from "./action-def.types";
import { arr, bool, deleteOutput, num, obj, str } from "./output-format.builders";

// ── Ticket-status option builders ────────────────────────────────────────────
// Single source of truth for ticket-status labels. The empty-option label
// changes per call site ("All" for filters, "No change" for mutations) but
// every consumer derives `allowedValues` from `DbEnum.CHAT_TICKET_STATUS_VALUES`
// so adding a new ticket status (e.g. `ARCHIVED`) only requires a schema
// regen + a one-line entry here — no hunting through duplicated arrays.

const TICKET_STATUS_LABELS: Record<DbEnum.ChatTicketStatus, string> = {
  [DbEnum.ChatTicketStatus.SUBMITTED]: "Submitted",
  [DbEnum.ChatTicketStatus.IN_PROGRESS]: "In Progress",
  [DbEnum.ChatTicketStatus.WAITING_ON_CUSTOMER]: "Waiting on Customer",
  [DbEnum.ChatTicketStatus.RESOLVED]: "Resolved"
};

const buildTicketStatusConstraints = (emptyLabel: string) => ({
  allowedValues: ["", ...DbEnum.CHAT_TICKET_STATUS_VALUES],
  allowedOptions: [
    { label: emptyLabel, value: "" },
    ...DbEnum.CHAT_TICKET_STATUS_VALUES.map((value) => ({
      label: TICKET_STATUS_LABELS[value],
      value
    }))
  ]
});

export const inboxActions: NexusApiActionDef[] = [
  {
    action: "inbox.list",
    label: "List Conversations",
    description: "List inbox conversations with optional filters",
    inputSchema: {
      status: {
        type: "string",
        description: "Filter by status (optional)",
        constraints: {
          allowedValues: ["", "OPEN", "CLOSED"],
          allowedOptions: [
            { label: "All", value: "" },
            { label: "Open", value: "OPEN" },
            { label: "Closed", value: "CLOSED" }
          ]
        }
      },
      ticketStatus: {
        type: "string",
        description: "Filter by ticket status (optional)",
        constraints: buildTicketStatusConstraints("All")
      },
      ticketStatusIn: {
        type: "string",
        description:
          "Filter by multiple ticket statuses — comma-separated list (e.g. `SUBMITTED,IN_PROGRESS`). Mutually exclusive with ticketStatus."
      },
      ticketStatusNot: {
        type: "string",
        description: "Exclude one ticket status (optional). Must not match ticketStatus.",
        constraints: buildTicketStatusConstraints("No exclusion")
      },
      lastMessageBefore: {
        type: "string",
        description:
          "Only return conversations whose last message arrived BEFORE this ISO-8601 timestamp (e.g. `2026-05-15T00:00:00Z`). Must be strictly after lastMessageAfter."
      },
      lastMessageAfter: {
        type: "string",
        description:
          "Only return conversations whose last message arrived AFTER this ISO-8601 timestamp."
      },
      lastMessageTypeIn: {
        type: "string",
        description:
          "Filter by who sent the last message — comma-separated list of `USER`, `AGENT`, `SYSTEM`. AGENT covers tool/function calls."
      },
      commentContains: {
        type: "string",
        description:
          "Only return conversations with at least one internal comment whose text contains this substring (case-insensitive, max 500 chars)."
      },
      commentNotContains: {
        type: "string",
        description:
          "Exclude conversations with any internal comment whose text contains this substring (case-insensitive, max 500 chars)."
      },
      deploymentId: { type: "string", description: "Filter by deployment ID (optional)" },
      page: { type: "number", description: "Page number (optional, default 0)" }
    },
    outputFormat: obj("Result", {
      data: arr("List of conversations"),
      hasNextPage: bool("Whether more pages exist"),
      page: num("Current page")
    })
  },
  {
    action: "inbox.get",
    label: "Get Conversation",
    description: "Get conversation details by ID",
    inputSchema: {
      chatId: { type: "string", description: "Conversation ID" }
    },
    outputFormat: obj("Conversation details")
  },
  {
    action: "inbox.search",
    label: "Search Conversations",
    description: "Search conversations by text query",
    inputSchema: {
      query: { type: "string", description: "Search query" },
      deploymentId: { type: "string", description: "Filter by deployment ID (optional)" }
    },
    outputFormat: obj("Search results")
  },
  {
    action: "inbox.updateStatus",
    label: "Update Conversation Status",
    description: "Update status, ticket status, or response handling mode",
    inputSchema: {
      chatId: { type: "string", description: "Conversation ID" },
      status: {
        type: "string",
        description: "Chat status (optional)",
        constraints: {
          allowedValues: ["", "OPEN", "RUNNING", "ARCHIVED"],
          allowedOptions: [
            { label: "No change", value: "" },
            { label: "Open", value: "OPEN" },
            { label: "Running", value: "RUNNING" },
            { label: "Archived", value: "ARCHIVED" }
          ]
        }
      },
      ticketStatus: {
        type: "string",
        description: "Ticket status (optional)",
        constraints: buildTicketStatusConstraints("No change")
      },
      responseHandling: {
        type: "string",
        description: "Response handling mode (optional)",
        constraints: {
          allowedValues: ["", "AUTO", "ON_APPROVAL", "MANUAL"],
          allowedOptions: [
            { label: "No change", value: "" },
            { label: "Auto", value: "AUTO" },
            { label: "On Approval", value: "ON_APPROVAL" },
            { label: "Manual", value: "MANUAL" }
          ]
        }
      }
    },
    outputFormat: obj("Updated conversation")
  },
  {
    action: "inbox.updateTopic",
    label: "Update Conversation Topic",
    description: "Update the topic of a conversation",
    inputSchema: {
      chatId: { type: "string", description: "Conversation ID" },
      topic: { type: "string", description: "New topic" }
    },
    outputFormat: obj("Updated conversation")
  },
  {
    action: "inbox.getMetadata",
    label: "Get Conversation Metadata",
    description: "Get the custom metadata object stored on a conversation",
    inputSchema: {
      chatId: { type: "string", description: "Conversation ID" }
    },
    outputFormat: obj("Conversation metadata")
  },
  {
    action: "inbox.updateMetadata",
    label: "Update Conversation Metadata",
    description:
      "Shallow-merge custom metadata into a conversation. A non-null value overwrites that key; a null value clears it.",
    inputSchema: {
      chatId: { type: "string", description: "Conversation ID" },
      metadata: {
        type: "object",
        description:
          "Metadata patch (JSON object). Keys with a non-null value overwrite; a null value clears the key."
      }
    },
    outputFormat: obj("Conversation metadata")
  },
  {
    action: "inbox.addComment",
    label: "Add Comment",
    description: "Add an internal comment to a conversation",
    inputSchema: {
      chatId: { type: "string", description: "Conversation ID" },
      content: { type: "string", description: "Comment content" }
    },
    outputFormat: obj("Updated conversation")
  },
  {
    action: "inbox.delete",
    label: "Delete Conversation",
    description: "Delete a conversation (soft delete)",
    inputSchema: {
      chatId: { type: "string", description: "Conversation ID" }
    },
    outputFormat: deleteOutput
  },
  {
    action: "inbox.getMessages",
    label: "Get Messages",
    description:
      "Fetch the conversation transcript (messages) for a chat. Returns each message's id, role (USER/AGENT/SYSTEM), content, status and createdAt, its public type and customer-visibility, and its attachments — `files` for documents, `images` for inbound photos — with pagination.",
    inputSchema: {
      chatId: { type: "string", description: "Conversation ID" },
      limit: { type: "number", description: "Max messages per page (optional, default 20)" },
      page: { type: "number", description: "Page number (optional, default 1)" }
    },
    // Declares every field `NexusApiNode.inboxGetMessages` emits. The handler is
    // the only writer of this shape and callers cannot override it — an edit to
    // a node's stored `outputFormat` is re-seeded from here — so a field the
    // handler returns and this object omits is unreachable downstream however
    // the node is configured (NEX-4689).
    outputFormat: obj("Result", {
      data: arr(
        "Messages",
        obj("Message", {
          id: str("Message ID"),
          role: str("Sender role: USER, AGENT, or SYSTEM"),
          type: str(
            "Public message classification: USER_MESSAGE, REPLY, TOOL_CALL, TOOL_RESULT, SYSTEM, …"
          ),
          customerVisible: bool(
            "Whether the customer saw this row, or it is agent-runtime plumbing"
          ),
          content: str("Message text content"),
          status: str("Message status"),
          createdAt: str("Creation timestamp (ISO 8601)"),
          files: arr(
            "Attached documents (everything that is not an inbound image), from the Knowledge and legacy File relations",
            obj("File", {
              id: str("Attachment ID"),
              name: str("File name as stored"),
              url: str("Permanent, unsigned download URL — null when the upload recorded none"),
              contentType: str(
                "MIME type derived from the file name — null when the name carries no extension the platform knows"
              )
            })
          ),
          images: arr("URLs of the inbound image/* attachments on this message", str("Image URL"))
        })
      ),
      meta: obj("Pagination metadata")
    })
  },
  {
    action: "inbox.setAssignedUsers",
    label: "Set Assigned Users",
    description: "Set which users are assigned to a conversation",
    inputSchema: {
      chatId: { type: "string", description: "Conversation ID" },
      userIds: { type: "string", description: "User IDs (comma-separated)" }
    },
    outputFormat: obj("Assignment result")
  },
  {
    action: "inbox.getAssignedUsers",
    label: "Get Assigned Users",
    description: "Get users assigned to a conversation",
    inputSchema: {
      chatId: { type: "string", description: "Conversation ID" }
    },
    outputFormat: obj("Assigned users result")
  }
];
